import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/apiClient';
import type { DataResponse } from '../types/api';
import { useAuth } from './AuthContext';
import { profileQueryKey } from './useProfile';

/** Known announcement keys — mirrors discovr-backend's
 *  internal/users.knownAnnouncementKeys allowlist. Add a new entry on both
 *  sides whenever a feature introduces another one-time announcement. */
export type AnnouncementKey = 'script_comment_intro' | 'shopify_next_steps' | 'brand_brain_tour';

/**
 * Marks a one-time in-app announcement/walkthrough as seen for the current
 * user (POST /profile/seen-announcements/{key}) — server-side, so "first
 * time only" holds per USER across devices/browsers, not per browser
 * profile the way a localStorage flag would. Idempotent on the backend;
 * invalidates the profile query on success so useProfile()'s next read
 * reflects the updated seen_announcements list.
 */
export function useMarkAnnouncementSeen() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (key: AnnouncementKey) =>
      apiClient
        .post<DataResponse<{ seen: boolean }>>(`/profile/seen-announcements/${key}`)
        .then((r) => r.data.data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: profileQueryKey(user?.uid) });
    },
  });
}
